/**
 * Volume discount bar.
 *
 * Shows the "buy more save more" ladder a Recharge bundle carries, outside the
 * bundle builder itself — in the cart drawer, say. The ladder comes from
 * Recharge at runtime, from the same public app proxy the bundle widget reads,
 * so it cannot drift from the app's configuration.
 *
 * `data-count` is how many bundle items the customer already has; without one
 * the bar is a static teaser.
 */
if (!customElements.get('bundle-tier-teaser')) {
  /**
   * One request per bundle per page. The cart drawer rebuilds its footer on
   * every cart change, so a fresh element is created each time — without this
   * each of those would hit the network again and blink while it waited.
   */
  const ladders = new Map();

  const loadLadder = (productId) => {
    if (!ladders.has(productId)) {
      ladders.set(
        productId,
        fetch('/tools/recurring/bundle-data/' + encodeURIComponent(productId), {
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .catch((error) => {
            // Let the next element try again rather than caching the failure.
            ladders.delete(productId);
            throw error;
          })
      );
    }
    return ladders.get(productId);
  };

  class BundleTierTeaser extends HTMLElement {
    async connectedCallback() {
      this.productId = (this.dataset.bundleProduct || '').trim();
      this.pillsEl = this.querySelector('[data-bt-pills]');
      this.fillEl = this.querySelector('[data-bt-fill]');
      this.noteEl = this.querySelector('[data-bt-note]');
      this.count = Math.max(0, parseInt(this.dataset.count, 10) || 0);
      if (!this.productId || !this.pillsEl) return;

      try {
        const data = await loadLadder(this.productId);
        if (!this.isConnected) return;
        this.render(this.tiersFrom(data.incentives));
      } catch (error) {
        console.warn('[bundle-tier-teaser] Could not read the Recharge bundle configuration.', error);
      }
    }

    tiersFrom(incentives) {
      const discount = ((incentives || {}).tiered_discounts || [])[0];
      return ((discount || {}).tiers || [])
        .map((entry) => entry.tier || entry)
        .filter((tier) => tier && tier.discount_type === 'percentage')
        .map((tier) => ({
          quantity: Math.max(1, parseInt(tier.condition_quantity_gte, 10) || 1),
          percent: Math.round(parseFloat(tier.discount_value) || 0),
        }))
        .sort((a, b) => a.quantity - b.quantity);
    }

    // Square brackets rather than braces: Shopify escapes braces in theme
    // settings, which is the convention the rest of this theme follows too.
    fill(text, values) {
      return Object.keys(values).reduce(
        (out, key) => out.split('[' + key + ']').join(values[key]),
        String(text || '')
      );
    }

    render(tiers) {
      if (!tiers.length) return;

      const template = this.dataset.pillText || '[quantity]+ · save [percent]';
      this.pillsEl.innerHTML = '';
      tiers.forEach((tier) => {
        const pill = document.createElement('span');
        pill.className = 'bundle-builder__tier';
        pill.textContent = this.fill(template, { quantity: tier.quantity, percent: tier.percent + '%' });
        pill.classList.toggle('is-reached', this.count >= tier.quantity);
        this.pillsEl.appendChild(pill);
      });

      const top = tiers[tiers.length - 1].quantity;
      if (this.fillEl) this.fillEl.style.width = Math.min(100, (this.count / top) * 100) + '%';

      if (this.noteEl) {
        const next = tiers.find((tier) => this.count < tier.quantity);
        const reached = tiers.filter((tier) => this.count >= tier.quantity).pop();
        if (next) {
          this.noteEl.textContent = this.fill(this.dataset.noteText, {
            count: next.quantity - this.count,
            percent: next.percent + '%',
          });
        } else if (reached) {
          this.noteEl.textContent = this.fill(this.dataset.noteComplete, { percent: reached.percent + '%' });
        }
      }

      this.hidden = false;
    }
  }

  customElements.define('bundle-tier-teaser', BundleTierTeaser);
}
